import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, HelpCircle, Shield, Phone, Info, Users, UserCheck, FileText, Scale, Activity, ArrowLeft, Briefcase } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import Images from "../../utils/Images";
import DropdownMenu from "./DropdownMenu";
import MegaMenu from "./MegaMenu";

// Base nav links
const baseNavLinks = [
  { name: "Home", path: "/" },
  { 
    name: "Support", 
    type: "dropdown",
    items: [
      {
        title: "Help Center",
        description: "Find answers to your questions",
        href: "/help-center",
        icon: HelpCircle
      },
      {
        title: "Safety Information",
        description: "Travel safety guidelines",
        href: "/safety-information",
        icon: Shield
      },
      {
        title: "Contact Us",
        description: "Get in touch with support",
        href: "/contact-us",
        icon: Phone
      }
    ]
  }
];

// Standard nav links (with Company and Partners)
const standardNavLinks = [
  ...baseNavLinks,
  { 
    name: "Company", 
    type: "dropdown",
    items: [
      {
        title: "About Us",
        description: "Learn about TripSecure",
        href: "/about-us",
        icon: Info
      },
      {
        title: "Privacy Policy",
        description: "How we protect your data",
        href: "/privacy-policy",
        icon: FileText
      },
      {
        title: "Terms of Service",
        description: "Our terms and conditions",
        href: "/terms-of-service",
        icon: Scale
      }
    ]
  },
  { 
    name: "Partners", 
    type: "dropdown",
    items: [
      {
        title: "Become a Provider",
        description: "Join our partner network",
        href: "/become-a-provider",
        icon: Users
      },
      {
        title: "Become an Agent",
        description: "Start earning as an agent",
        href: "/become-an-agent",
        icon: UserCheck
      }
    ]
  }
];

// Media events base nav links (with Media & Events specific Support)
const mediaEventsBaseNavLinks = [
  { name: "Home", path: "/media-events" },
  { 
    name: "Support", 
    type: "dropdown",
    items: [
      {
        title: "Media & Events Overview",
        description: "Learn about TripSecure Media & Events",
        href: "/media-events-overview",
        icon: Info
      },
      {
        title: "Help Center – Media & Events",
        description: "Find answers to your questions",
        href: "/help-center-media-events",
        icon: HelpCircle
      },
      {
        title: "Contact Us – Media & Events",
        description: "Get in touch with support",
        href: "/contact-us-media-events",
        icon: Phone
      }
    ]
  },
  { 
    name: "Company", 
    type: "dropdown",
    items: [
      {
        title: "About Us",
        description: "Learn about TripSecure",
        href: "/about-us",
        icon: Info
      },
      {
        title: "Privacy Policy",
        description: "How we protect your data",
        href: "/privacy-policy",
        icon: FileText
      },
      {
        title: "Terms of Service",
        description: "Our terms and conditions",
        href: "/terms-of-service",
        icon: Scale
      }
    ]
  },
  { 
    name: "Partner", 
    type: "dropdown",
    items: [
      {
        title: "Become a partner",
        description: "Partner information coming soon",
        href: null,
        icon: Briefcase,
        disabled: true
      },
      {
        title: "Become an agent",
        description: "Start earning as an agent",
        href: "/become-an-agent",
        icon: UserCheck
      }
    ]
  }
];

// Media events nav links (with My-Activity)
const mediaEventsNavLinks = [
  ...mediaEventsBaseNavLinks,
  { 
    name: "My-Activity", 
    type: "dropdown",
    items: [
      {
        title: "Activity History",
        description: "View your recent activity",
        href: "/my-activity",
        icon: Activity
      }
    ]
  }
];

// Mobile Dropdown Menu Component
const MobileDropdownMenu = ({ title, items, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="space-y-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-between items-center px-4 py-3 w-full text-left text-gray-700 rounded-lg transition-colors hover:bg-gray-50 hover:text-green-600"
      >
        <div className="flex gap-3 items-center">
          {Icon && <Icon className="w-4 h-4" />}
          <span className="font-medium">{title}</span>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="ml-4 space-y-1"
          >
            {items.map((item, index) => {
              const isDisabled = item.disabled || !item.href;
              
              return (
                <div key={index}>
                  {isDisabled ? (
                    <div className="flex gap-3 items-center px-4 py-2 text-sm text-gray-400 rounded-lg cursor-not-allowed opacity-60">
                      {item.icon && <item.icon className="w-4 h-4" />}
                      <div>
                        <div className="font-medium">{item.title}</div>
                        {item.description && (
                          <div className="text-xs text-gray-400">{item.description}</div>
                        )}
                      </div>
                    </div>
                  ) : item.external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex gap-3 items-center px-4 py-2 text-sm text-gray-600 rounded-lg transition-colors hover:bg-green-50 hover:text-green-600"
                    >
                      {item.icon && <item.icon className="w-4 h-4" />}
                      <div>
                        <div className="font-medium">{item.title}</div>
                        {item.description && (
                          <div className="text-xs text-gray-500">{item.description}</div>
                        )}
                      </div>
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      className="flex gap-3 items-center px-4 py-2 text-sm text-gray-600 rounded-lg transition-colors hover:bg-green-50 hover:text-green-600"
                    >
                      {item.icon && <item.icon className="w-4 h-4" />}
                      <div>
                        <div className="font-medium">{item.title}</div>
                        {item.description && (
                          <div className="text-xs text-gray-500">{item.description}</div>
                        )}
                      </div>
                    </Link>
                  )}
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function GuestNavbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isVerified } = useAppSelector((s) => s.auth);
  
  // Check if we're on media-events or conference-trade-mission pages
  const isDirectMediaEventsPage = location.pathname.startsWith("/media-events") || 
                                   location.pathname.startsWith("/conference-trade-mission")||
                                   location.pathname.startsWith("/events") ||
                                   location.pathname.startsWith("/event-details") ||
                                   location.pathname.startsWith("/my-event-registrations") ||
                                   location.pathname.startsWith("/my-registrations") ||
                                   location.pathname.startsWith("/conference-registration") ||
                                   location.pathname.startsWith("/conference-registrations") ||
                                   location.pathname.startsWith("/media-events-overview") ||
                                   location.pathname.startsWith("/help-center-media-events") ||
                                   location.pathname.startsWith("/contact-us-media-events");
  
  // Check if we're on a Company page and came from Media & Events
  const isCompanyPage = location.pathname === "/about-us" || 
                        location.pathname === "/privacy-policy" || 
                        location.pathname === "/terms-of-service";
  const isFromMediaEvents = sessionStorage.getItem("isMediaEventsContext") === "true";
  
  // Determine if we should show Media & Events navbar
  const isMediaEventsPage = isDirectMediaEventsPage || (isCompanyPage && isFromMediaEvents);
  
  // Set Media & Events context when on Media & Events pages
  useEffect(() => {
    if (isDirectMediaEventsPage) {
      sessionStorage.setItem("isMediaEventsContext", "true");
    } else if (isCompanyPage && !isFromMediaEvents) {
      // Clear context if on Company page but didn't come from Media & Events
      sessionStorage.removeItem("isMediaEventsContext");
    } else if (!isCompanyPage && !isDirectMediaEventsPage && location.pathname === "/") {
      // Clear context when navigating to main home page
      sessionStorage.removeItem("isMediaEventsContext");
    }
  }, [isDirectMediaEventsPage, isCompanyPage, isFromMediaEvents, location.pathname]);
  
  const isAuthenticatedUser = isAuthenticated && isVerified;
  
  // Determine which nav links to show
  let navLinks;
  if (isMediaEventsPage) {
    // On media-events pages: show media events base links if not authenticated, add My-Activity if authenticated
    navLinks = isAuthenticatedUser ? mediaEventsNavLinks : mediaEventsBaseNavLinks;
  } else {
    // On other pages: show standard links (with Company and Partners)
    navLinks = standardNavLinks;
  }
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollY / windowHeight) * 100;

      setIsScrolled(scrollY > 20);
      setScrollProgress(Math.min(progress, 100));
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActiveLink = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    if (path === "/media-events") {
      // For media-events home, match exactly or paths that start with /media-events/
      return location.pathname === "/media-events" || location.pathname.startsWith("/media-events/");
    }
    return location.pathname.startsWith(path);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) {
      document.body.style.overflow = "unset";
    } else {
      document.body.style.overflow = "hidden";
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = "unset";
  };

  return (
    <>
      {/* Desktop Navbar */}
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "border-b shadow-lg backdrop-blur-md bg-white/95 border-gray-200/50"
            : "backdrop-blur-md bg-black/95"
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{
          boxShadow: isScrolled
            ? "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        }}
      >
        {/* Scroll Progress Bar */}
        <motion.div
          className="absolute top-0 left-0 z-10 h-1 bg-gradient-to-r from-green-500 to-emerald-500"
          style={{ width: `${scrollProgress}%` }}
          initial={{ width: 0 }}
          transition={{ duration: 0.1 }}
        />
        <div className="px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <img
                  src={Images.logo_navbar}
                  alt="TripSecure"
                  className={`h-7 transition-all duration-300 ${
                    isScrolled
                      ? "filter brightness-0"
                      : "filter brightness-0 invert"
                  }`}
                />
              </Link>
            </div>

            {/* Desktop Navigation Links */}
            <div className="hidden lg:flex lg:items-center lg:space-x-8">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="relative group"
                >
                  {link.type === "dropdown" ? (
                    <DropdownMenu
                      title={link.name}
                      items={link.items}
                      icon={link.icon}
                      isScrolled={isScrolled}
                    />
                  ) : link.type === "mega" ? (
                    <MegaMenu
                      title={link.name}
                      sections={link.sections}
                      icon={link.icon}
                      isScrolled={isScrolled}
                    />
                  ) : (
                    <Link
                      to={link.path}
                      className={`relative px-3 py-2 text-sm font-medium transition-all duration-300 ${
                        isScrolled
                          ? isActiveLink(link.path)
                            ? "text-green-600"
                            : "text-gray-700 hover:text-green-600"
                          : isActiveLink(link.path)
                          ? "text-green-400"
                          : "text-white hover:text-green-400"
                      }`}
                    >
                      {link.name}
                      {/* Active indicator */}
                      {isActiveLink(link.path) && (
                        <motion.div
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-500"
                          layoutId="activeTab"
                          initial={false}
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 30,
                          }}
                        />
                      )}
                      {/* Hover underline */}
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-current"
                        initial={{ scaleX: 0 }}
                        whileHover={{ scaleX: 1 }}
                        transition={{ duration: 0.3 }}
                        style={{ transformOrigin: "left" }}
                      />
                    </Link>
                  )}
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex lg:items-center lg:space-x-4">
              <motion.button
                onClick={()=>navigate("/signin")}
                className={`relative px-6 py-2.5 rounded-lg font-medium transition-all duration-300 border-2 ${
                  isScrolled
                    ? "border-gray-300 text-gray-700 hover:bg-gray-50"
                    : "border-white/30 text-white hover:bg-white/10"
                }`}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                Sign In
              </motion.button>
              <motion.button
                onClick={()=>navigate("/signup")}
                className={`relative px-6 py-2.5 rounded-lg font-medium transition-all duration-300 ${
                  isScrolled
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : "bg-green-500 text-white hover:bg-green-600"
                }`}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                Get Started
                {/* Notification Badge */}
                <motion.div
                  className="absolute -top-2 -right-2 w-3 h-3 bg-red-500 rounded-full"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    delay: 1,
                    type: "spring",
                    stiffness: 400,
                    damping: 10,
                  }}
                />
              </motion.button>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <motion.button
                onClick={toggleMobileMenu}
                className={`p-2 rounded-md transition-all duration-300 ${
                  isScrolled
                    ? "text-gray-700 hover:text-green-600 hover:bg-gray-100"
                    : "text-white hover:text-green-400 hover:bg-white/10"
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Menu size={24} />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 z-40 backdrop-blur-sm bg-black/50 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={closeMobileMenu}
            />

            {/* Mobile Menu */}
            <motion.div
              className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-50 lg:hidden"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="flex overflow-hidden flex-col h-full">
                {/* Mobile Menu Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                  <img
                    src={Images.logo_navbar}
                    alt="TripSecure"
                    className="h-8 filter brightness-0"
                  />
                  <motion.button
                    onClick={closeMobileMenu}
                    className="p-2 text-gray-500 rounded-md hover:text-gray-700 hover:bg-gray-100"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X size={24} />
                  </motion.button>
                </div>

                {/* Mobile Navigation Links */}
                <nav className="overflow-y-auto flex-1 px-6 py-6 space-y-2">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.name}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.1, duration: 0.3 }}
                    >
                      {link.type === "dropdown" || link.type === "mega" ? (
                        <MobileDropdownMenu
                          title={link.name}
                          items={link.items || link.sections}
                          icon={link.icon}
                        />
                      ) : (
                        <Link
                          to={link.path}
                          onClick={closeMobileMenu}
                          className={`block px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                            isActiveLink(link.path)
                              ? "bg-green-50 text-green-700 border-l-4 border-green-500"
                              : "text-gray-700 hover:bg-gray-50 hover:text-green-600"
                          }`}
                        >
                          {link.name}
                        </Link>
                      )}
                    </motion.div>
                  ))}
                </nav>

                {/* Mobile CTA */}
                <div className="p-6 space-y-3 border-t border-gray-200">
                  <motion.button
                    onClick={()=>navigate("/signup")}
                    className="px-6 py-3 w-full font-medium text-white bg-green-600 rounded-lg shadow-lg transition-all duration-300 hover:bg-green-700"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Get Started
                  </motion.button>
                  <motion.button
                    onClick={()=>navigate("/signin")}
                    className="px-6 py-3 w-full font-medium text-gray-700 bg-gray-100 rounded-lg transition-all duration-300 hover:bg-gray-200"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Sign In
                  </motion.button>
                  <p className="text-xs text-center text-gray-500">
                    Join thousands of travelers
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Tablet Navigation (Hidden on mobile and desktop) */}
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 md:block lg:hidden ${
          isScrolled
            ? "border-b shadow-lg backdrop-blur-md bg-white/95 border-gray-200/50"
            : "backdrop-blur-md bg-black/95"
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{
          boxShadow: isScrolled
            ? "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        }}
      >
        {/* Scroll Progress Bar */}
        <motion.div
          className="absolute top-0 left-0 z-10 h-1 bg-gradient-to-r from-green-500 to-emerald-500"
          style={{ width: `${scrollProgress}%` }}
          initial={{ width: 0 }}
          transition={{ duration: 0.1 }}
        />
        <div className="px-4 mx-auto max-w-4xl sm:px-6">
          <div className="flex justify-between items-center h-14">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <img
                  src={Images.logo_navbar}
                  alt="TripSecure"
                  className={`h-7 transition-all duration-300 ${
                    isScrolled
                      ? "filter brightness-0"
                      : "filter brightness-0 invert"
                  }`}
                />
              </Link>
            </div>

            {/* Tablet Navigation Links */}
            <div className="hidden md:flex md:items-center md:space-x-6">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  {link.type === "dropdown" ? (
                    <DropdownMenu
                      title={link.name}
                      items={link.items}
                      icon={link.icon}
                      isScrolled={isScrolled}
                    />
                  ) : link.type === "mega" ? (
                    <MegaMenu
                      title={link.name}
                      sections={link.sections}
                      icon={link.icon}
                      isScrolled={isScrolled}
                    />
                  ) : (
                    <Link
                      to={link.path}
                      className={`relative px-2 py-2 text-sm font-medium transition-all duration-300 ${
                        isScrolled
                          ? isActiveLink(link.path)
                            ? "text-green-600"
                            : "text-gray-700 hover:text-green-600"
                          : isActiveLink(link.path)
                          ? "text-green-400"
                          : "text-white hover:text-green-400"
                      }`}
                    >
                      {link.name}
                      {isActiveLink(link.path) && (
                        <motion.div
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-500"
                          layoutId="activeTabTablet"
                          initial={false}
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 30,
                          }}
                        />
                      )}
                    </Link>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Tablet CTA */}
            <div className="hidden md:flex md:items-center md:space-x-3">
              <motion.button
                onClick={()=>navigate("/signin")}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 border-2 ${
                  isScrolled
                    ? "text-gray-700 border-gray-300 hover:bg-gray-50"
                    : "text-white border-white/30 hover:bg-white/10"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                Sign In
              </motion.button>
              <motion.button
                onClick={()=>navigate("/signup")}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                  isScrolled
                    ? "text-white bg-green-600 hover:bg-green-700"
                    : "text-white bg-green-500 hover:bg-green-600"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                Get Started
              </motion.button>
            </div>

            {/* Mobile menu button for tablet */}
            <div className="md:hidden">
              <motion.button
                onClick={toggleMobileMenu}
                className={`p-2 rounded-md transition-all duration-300 ${
                  isScrolled
                    ? "text-gray-700 hover:text-green-600 hover:bg-gray-100"
                    : "text-white hover:text-green-400 hover:bg-white/10"
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Menu size={24} />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Floating Action Button for Mobile */}
      <motion.div
        className="fixed right-6 bottom-6 z-40 lg:hidden"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 400, damping: 10 }}
      >
        <motion.button
          className="flex justify-center items-center w-14 h-14 text-white bg-green-600 rounded-full shadow-2xl"
          whileHover={{ scale: 1.1, rotate: 5 }}
          whileTap={{ scale: 0.9 }}
          onClick={toggleMobileMenu}
        >
          <Menu size={24} />
        </motion.button>
      </motion.div>
    </>
  );
}
